<template>
  <div class="onboarding-screen">
    <div class="illustration">
      <img :src="image" :alt="`${title} Illustration`" />
    </div>
    <div class="content">
      <div class="text">
        <h3>{{ title }}</h3>
        <p v-html="description"></p>
        <div v-if="step === 2">
          <p>
            By clicking “Connect” you agree to
            <a href="https://webflow.com/legal/labs-terms" target="_blank" rel="noopener noreferrer"
              >Webflow Labs Terms of Service</a
            >
            and
            <a href="https://webflow.com/legal/privacy" target="_blank" rel="noopener noreferrer"
              >Privacy Policy</a
            >.
          </p>
          <div class="checkbox-wrapper">
            <input
              type="checkbox"
              id="email-updates"
              name="email-updates"
              checked
              v-model="$parent.hasOptIn"
            />
            <label for="email-updates">Receive email updates</label>
          </div>
        </div>
      </div>
      <div class="actions" :class="{ 'justify-between': step === 2 }">
        <button
          v-if="hasPrev"
          class="button"
          :class="{ hide: isLoading }"
          @click="$emit('previous-clicked')"
        >
          <WfIcon icon="back-arrow" />
          Back
        </button>
        <button v-if="hasNext" class="button" @click="$emit('next-clicked')">
          Next
          <WfIcon icon="forward-arrow" />
        </button>
        <button
          v-if="hasGetStarted"
          class="button is-primary"
          :class="{ disabled: isLoading }"
          @click="$emit('get-started')"
        >
          <span v-text="isLoading ? 'Getting started...' : 'Connect'"></span>
          <div v-if="isLoading" class="icon icon--spinner icon--small">
            <svg
              fill="none"
              height="16"
              width="16"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clip-rule="evenodd"
                d="m15.1645 8.52993c.0489.27178-.1318.53173-.4036.58061-1.3104.23568-2.5265.84022-3.5054 1.74266-.979.9025-1.68029 2.0654-2.0216 3.3524s-.3085 2.6446.09459 3.9136 1.15971 2.3967 2.18121 3.2508c1.0214.8541 2.2652 1.3992 3.5855 1.5713s2.6623-.0359 3.8685-.5997c1.2063-.5637 2.2267-1.4598 2.9416-2.583.715-1.1233 1.0947-2.4271 1.0947-3.7586 0-.2761.2239-.5.5-.5s.5.2239.5.5c0 1.5217-.434 3.0118-1.251 4.2955-.8171 1.2837-1.9833 2.3078-3.3619 2.9521-1.3785.6442-2.9122.882-4.4211.6853-1.509-.1967-2.9305-.8196-4.0978-1.7958-1.16736-.9761-2.0321-2.2649-2.49278-3.7152-.46068-1.4502-.49818-3.0018-.10811-4.4726.39007-1.4709 1.19154-2.8 2.31039-3.8313 1.1188-1.03141 2.5086-1.72232 4.0062-1.99167.2718-.04888.5317.13181.5806.4036z"
                fill="#000"
                fill-rule="evenodd"
              />
            </svg>
          </div>
        </button>
        <button
          v-if="hasStart"
          class="button is-primary"
          :class="{ disabled: isLoading }"
          @click="$emit('start')"
        >
          <span v-text="isLoading ? 'Authenticating...' : 'Get started'"></span>
          <div v-if="isLoading" class="icon icon--spinner icon--small">
            <svg
              fill="none"
              height="16"
              width="16"
              viewBox="0 0 16 16"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                clip-rule="evenodd"
                d="m15.1645 8.52993c.0489.27178-.1318.53173-.4036.58061-1.3104.23568-2.5265.84022-3.5054 1.74266-.979.9025-1.68029 2.0654-2.0216 3.3524s-.3085 2.6446.09459 3.9136 1.15971 2.3967 2.18121 3.2508c1.0214.8541 2.2652 1.3992 3.5855 1.5713s2.6623-.0359 3.8685-.5997c1.2063-.5637 2.2267-1.4598 2.9416-2.583.715-1.1233 1.0947-2.4271 1.0947-3.7586 0-.2761.2239-.5.5-.5s.5.2239.5.5c0 1.5217-.434 3.0118-1.251 4.2955-.8171 1.2837-1.9833 2.3078-3.3619 2.9521-1.3785.6442-2.9122.882-4.4211.6853-1.509-.1967-2.9305-.8196-4.0978-1.7958-1.16736-.9761-2.0321-2.2649-2.49278-3.7152-.46068-1.4502-.49818-3.0018-.10811-4.4726.39007-1.4709 1.19154-2.8 2.31039-3.8313 1.1188-1.03141 2.5086-1.72232 4.0062-1.99167.2718-.04888.5317.13181.5806.4036z"
                fill="#000"
                fill-rule="evenodd"
              />
            </svg>
          </div>
        </button>
        <button v-if="hasError" class="button is-primary" @click="$emit('try-again')">
          Try again
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import WfIcon from '@/components/WfIcon.vue';

defineProps([
  'image',
  'title',
  'description',
  'hasPrev',
  'hasNext',
  'hasGetStarted',
  'hasStart',
  'hasError',
  'step',
  'isLoading',
]);
</script>
